
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface EmailLog {
  id: string;
  email_type: string;
  recipient_email?: string;
  user_id?: string;
  status: string;
  subject?: string;
  error?: string;
  sent_at: string;
  profiles?: {
    email?: string;
    name?: string;
  };
}

export interface EmailLogsResponse {
  logs: EmailLog[];
  total: number;
}

export const useEmailLogs = (page: number = 1, pageSize: number = 10) => {
  return useQuery({
    queryKey: ['email-logs', page, pageSize],
    queryFn: async (): Promise<EmailLogsResponse> => {
      console.log(`Fetching email logs - page: ${page}, pageSize: ${pageSize}`);
      
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // First, let's just fetch the email logs without the profiles join to avoid foreign key issues
      const { data: logs, error, count } = await supabase
        .from('email_logs')
        .select('*', { count: 'exact' })
        .order('sent_at', { ascending: false })
        .range(from, to);

      if (error) {
        console.error('Error fetching email logs:', error);
        throw new Error(`Failed to fetch email logs: ${error.message}`);
      }

      // If we have logs with user_ids, fetch the profile information separately
      const enrichedLogs = await Promise.all(
        (logs || []).map(async (log) => {
          let profileData = null;
          
          if (log.user_id) {
            try {
              const { data: profile } = await supabase
                .from('profiles')
                .select('email, name')
                .eq('id', log.user_id)
                .single();
              
              if (profile) {
                profileData = profile;
              }
            } catch (profileError) {
              console.warn(`Could not fetch profile for user ${log.user_id}:`, profileError);
            }
          }
          
          return {
            ...log,
            profiles: profileData
          };
        })
      );

      console.log(`Successfully fetched ${enrichedLogs?.length || 0} email logs`);

      return {
        logs: enrichedLogs || [],
        total: count || 0
      };
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
  });
};
